import type { AtributoDocumento, CategoriaDocumento, TipoDocumento } from '../types'

export const CATEGORIAS_DOCUMENTO: CategoriaDocumento[] = [
  'Documentos del despacho',
  'Documentos procesales',
  'Resoluciones de la autoridad',
  'Pruebas y anexos',
  'Administrativos',
]

export const ATRIBUTOS_DOCUMENTO: {
  clave: AtributoDocumento
  etiqueta: string
  ayuda: string
}[] = [
  {
    clave: 'fechaAcuse',
    etiqueta: 'Fecha de acuse / presentación',
    ayuda: 'Fecha sellada por la autoridad al recibir el documento.',
  },
  {
    clave: 'fojas',
    etiqueta: 'Número de fojas',
    ayuda: 'Cantidad de hojas útiles que integra el documento.',
  },
  {
    clave: 'autoridadEmisora',
    etiqueta: 'Autoridad emisora',
    ayuda: 'Juzgado, fiscalía, notaría o dependencia que emite el documento.',
  },
  {
    clave: 'clasificacion',
    etiqueta: '¿Público / Privado / Copia certificada?',
    ayuda: 'Naturaleza del ejemplar que se resguarda en el expediente físico.',
  },
  {
    clave: 'vigencia',
    etiqueta: 'Vigencia / fecha de expiración',
    ayuda: 'Hasta cuándo es válido el documento.',
  },
  {
    clave: 'firmaCedula',
    etiqueta: 'Firma / cédula del abogado',
    ayuda: 'Cédula profesional de quien suscribe el documento.',
  },
  {
    clave: 'cadenaCustodia',
    etiqueta: 'Cadena de custodia',
    ayuda: 'Folio de registro de custodia del material probatorio.',
  },
]

export function etiquetaAtributo(clave: AtributoDocumento): string {
  return ATRIBUTOS_DOCUMENTO.find((a) => a.clave === clave)?.etiqueta ?? clave
}

/** Catálogo de ejemplo. Todo es editable desde Configuración → Catálogo de documentos. */
export const TIPOS_DOCUMENTO_INICIALES: TipoDocumento[] = [
  // Documentos del despacho
  {
    id: 'td-01',
    nombre: 'Contrato de prestación de servicios',
    categoria: 'Documentos del despacho',
    atributos: ['fojas', 'vigencia', 'firmaCedula'],
    requiereVencimiento: false,
    confidencial: true,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-02',
    nombre: 'Carta de honorarios',
    categoria: 'Documentos del despacho',
    atributos: ['fojas', 'firmaCedula'],
    requiereVencimiento: false,
    confidencial: true,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-03',
    nombre: 'Poder / carta poder',
    categoria: 'Documentos del despacho',
    atributos: ['fojas', 'autoridadEmisora', 'vigencia', 'clasificacion'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-04',
    nombre: 'Identificación del cliente',
    categoria: 'Documentos del despacho',
    atributos: ['vigencia'],
    requiereVencimiento: false,
    confidencial: true,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },

  // Documentos procesales
  {
    id: 'td-05',
    nombre: 'Demanda',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas', 'autoridadEmisora', 'clasificacion', 'firmaCedula'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: false,
    materias: ['Civil', 'Mercantil', 'Laboral', 'Familiar', 'Administrativo', 'Amparo'],
    activo: true,
  },
  {
    id: 'td-06',
    nombre: 'Denuncia',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas', 'autoridadEmisora', 'firmaCedula'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: false,
    materias: ['Penal'],
    activo: true,
  },
  {
    id: 'td-07',
    nombre: 'Querella',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas', 'autoridadEmisora', 'firmaCedula', 'cadenaCustodia'],
    requiereVencimiento: true,
    confidencial: true,
    aplicaTodas: false,
    materias: ['Penal'],
    activo: true,
  },
  {
    id: 'td-08',
    nombre: 'Contestación',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas', 'firmaCedula'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-09',
    nombre: 'Promoción',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-10',
    nombre: 'Ofrecimiento de pruebas',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas', 'clasificacion'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-11',
    nombre: 'Alegatos',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-12',
    nombre: 'Oficio',
    categoria: 'Documentos procesales',
    atributos: ['fechaAcuse', 'fojas', 'autoridadEmisora'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },

  // Resoluciones de la autoridad
  {
    id: 'td-13',
    nombre: 'Acuerdo',
    categoria: 'Resoluciones de la autoridad',
    atributos: ['fojas', 'autoridadEmisora'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-14',
    nombre: 'Auto',
    categoria: 'Resoluciones de la autoridad',
    atributos: ['fojas', 'autoridadEmisora'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-15',
    nombre: 'Sentencia',
    categoria: 'Resoluciones de la autoridad',
    atributos: ['fojas', 'autoridadEmisora', 'clasificacion'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-16',
    nombre: 'Laudo',
    categoria: 'Resoluciones de la autoridad',
    atributos: ['fojas', 'autoridadEmisora', 'clasificacion'],
    requiereVencimiento: true,
    confidencial: false,
    aplicaTodas: false,
    materias: ['Laboral'],
    activo: true,
  },

  // Pruebas y anexos
  {
    id: 'td-17',
    nombre: 'Prueba documental',
    categoria: 'Pruebas y anexos',
    atributos: ['fojas', 'clasificacion', 'cadenaCustodia'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-18',
    nombre: 'Prueba pericial',
    categoria: 'Pruebas y anexos',
    atributos: ['fojas', 'autoridadEmisora', 'firmaCedula', 'cadenaCustodia'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-19',
    nombre: 'Prueba testimonial',
    categoria: 'Pruebas y anexos',
    atributos: ['fojas', 'cadenaCustodia'],
    requiereVencimiento: false,
    confidencial: true,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-20',
    nombre: 'Factura',
    categoria: 'Pruebas y anexos',
    atributos: ['fojas', 'clasificacion'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-21',
    nombre: 'Escritura pública',
    categoria: 'Pruebas y anexos',
    atributos: ['fojas', 'autoridadEmisora', 'clasificacion'],
    requiereVencimiento: false,
    confidencial: true,
    aplicaTodas: false,
    materias: ['Civil', 'Mercantil', 'Familiar'],
    activo: true,
  },
  {
    id: 'td-22',
    nombre: 'Fotografía',
    categoria: 'Pruebas y anexos',
    atributos: ['cadenaCustodia'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },

  // Administrativos
  {
    id: 'td-23',
    nombre: 'Minuta de reunión',
    categoria: 'Administrativos',
    atributos: ['fojas'],
    requiereVencimiento: false,
    confidencial: true,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-24',
    nombre: 'Acuse',
    categoria: 'Administrativos',
    atributos: ['fechaAcuse', 'fojas'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
  {
    id: 'td-25',
    nombre: 'Recibo',
    categoria: 'Administrativos',
    atributos: ['fojas'],
    requiereVencimiento: false,
    confidencial: false,
    aplicaTodas: true,
    materias: [],
    activo: false,
  },
  {
    id: 'td-26',
    nombre: 'Factura de honorarios',
    categoria: 'Administrativos',
    atributos: ['fojas', 'vigencia'],
    requiereVencimiento: true,
    confidencial: true,
    aplicaTodas: true,
    materias: [],
    activo: true,
  },
]
